/**
 * GateWarningsResume — a small resume of the warnings/flags raised by an
 * intermediate gate's upstream agents (parties for Gate 1; compliance +
 * signature for Gate 2). Gives the reviewer the "if applicable" warning
 * context at the gate without opening each agent tab. Renders nothing when
 * the group has no warnings.
 */
import { AlertTriangle } from 'lucide-react';
import { STAGE_LABELS, type PausedStage } from '@/types/enums';
import { agentWarnings, latestAttemptByStage } from './StageHistoryRowHelpers';
import { upstreamStagesFor } from './gateGroups';
import type { AgentResult } from '@/types';

interface Props {
  /** 'gate_1' | 'gate_2'. Anything else renders nothing. */
  gate: string;
  agents: AgentResult[];
}

interface WarningGroup {
  stage: PausedStage;
  items: string[];
}

export function GateWarningsResume({ gate, agents }: Props) {
  const byStage = latestAttemptByStage(agents);
  const groups: WarningGroup[] = [];
  for (const stage of upstreamStagesFor(gate)) {
    const row = byStage.get(stage);
    const items = row ? agentWarnings(stage, row.output ?? null) : [];
    if (items.length > 0) groups.push({ stage, items });
  }

  if (groups.length === 0) return null;

  return (
    <div className="space-y-2 rounded border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-950/20">
      <p className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
        Warnings resume
      </p>
      {groups.map(({ stage, items }) => (
        <div key={stage} className="text-xs">
          <p className="font-medium text-amber-900 dark:text-amber-200">{STAGE_LABELS[stage]}</p>
          <ul className="list-inside list-disc text-amber-900/90 dark:text-amber-200/90">
            {items.map((it, i) => (
              <li key={i}>{it}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
